/**
 * Creates PCM16 data from an AudioBuffer
 *
 * This function converts Web Audio API AudioBuffer data into raw PCM16 format
 * (16-bit signed integer samples) without any file headers. This is more efficient
 * than WAV format for audio processing since it eliminates header overhead.
 *
 * @param audioBuffer - The AudioBuffer to convert to PCM16 format. This should contain
 *                      audio data from the Web Audio API (e.g., from MediaRecorder, AudioContext, etc.)
 *
 * @returns An ArrayBuffer containing the raw PCM16 audio data.
 *          The audio is encoded as 16-bit signed integers at the original sample rate and channel count.
 *
 * @example
 * ```typescript
 * // Convert an AudioBuffer to PCM16 format
 * const audioBuffer = audioContext.createBuffer(2, 44100, 44100); // 2 channels, 44.1kHz, 1 second
 * const pcm16Buffer = createPCM16FromAudioBuffer(audioBuffer);
 *
 * // Create a Blob for file download or processing
 * const blob = new Blob([pcm16Buffer], { type: 'audio/pcm' });
 *
 * // Or use directly with other APIs
 * const audioData = new Int16Array(pcm16Buffer);
 * ```
 *
 * @remarks
 * - The function generates raw PCM16 data without any file headers
 * - Supports any number of channels (mono, stereo, surround, etc.)
 * - Preserves the original sample rate from the AudioBuffer
 * - Output is little-endian 16-bit signed integers
 * - No header overhead, making it more efficient for processing
 * - Each sample is 2 bytes (16-bit)
 */
export const createPCM16FromAudioBuffer = (audioBuffer: AudioBuffer): ArrayBuffer => {
	// Extract audio properties from the buffer
	const numChannels = audioBuffer.numberOfChannels;
	const length = audioBuffer.length; // Total number of samples

	// Validate that we're working with mono audio as expected
	// Note: This function is designed to work with mono audio (1 channel) as configured in getUserMedia

	// Calculate buffer size: audio data only (no headers)
	// Each sample is 16-bit (2 bytes), multiplied by number of channels
	const buffer = new ArrayBuffer(length * numChannels * 2);
	const view = new DataView(buffer);

	// Write audio data starting at byte 0
	let offset = 0;

	// Convert each channel's float32 data to int16
	for (let channel = 0; channel < numChannels; channel++) {
		const channelData = audioBuffer.getChannelData(channel);

		for (let sample = 0; sample < length; sample++) {
			// Convert float32 (-1.0 to 1.0) to int16 (-32768 to 32767)
			// Clamp to prevent overflow
			const floatSample = Math.max(-1.0, Math.min(1.0, channelData[sample]));
			const int16Sample = Math.round(floatSample * 32767);

			// Write as little-endian 16-bit signed integer
			view.setInt16(offset, int16Sample, true); // true = little-endian
			offset += 2;
		}
	}

	return buffer;
};
